import { Button, Dropdown } from 'antd';
import clsx from 'clsx';
import * as eva from 'eva-icons';
import { isNaN, nth } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../AppContext';
import Error404 from '../../../../Error404';
import { EditIcon, Export, ForwardArrow } from '../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  GA_EVENT,
  GA_LABEL,
  ROUTES,
} from '../../../../common/constants';
import { getValidToken } from '../../../../common/tokenManager';
import { Event } from '../../../../common/trackEvents';
import useRouter from '../../../../common/useRouter';
import EllipsisText from '../../../../components/EllipsisText';
import history from '../../../../historyData';
import AddEditEqcModal from '../../AddEditEqcModal';
import { GET_EQC_TYPE } from '../../graphql/Queries';
import EqcDetailContent from './EqcDetailContent';

const EqcDetails = () => {
  const { getCurrentUser, getCurrentTenant, getTenantUser } =
    useContext(AppContext);
  const currentUser = getCurrentUser();
  const tenantId = getCurrentTenant() || getTenantUser()?.tenant?.id;
  const [showModal, setShowModal] = useState(false);
  const [eqcData, setEqcData] = useState();
  const {
    navigate,
    location,
    params: { eqcTypeId },
  } = useRouter();
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const navSelectedRowKeys = location?.state?.navSelectedRowKeys;
  const navSelectedRows = location?.state?.navSelectedRows;
  const navIsSelectAllAcrossPages = location?.state?.navIsSelectAllAcrossPages;
  const navExceptIds = location?.state?.navExceptIds;

  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [webEqcTypeList, { data: eqcTypeData, refetch }] =
    useLazyQueryWithCallbacks(GET_EQC_TYPE, { fetchPolicy: 'network-only' });

  useEffect(() => {
    const unlisten = history.listen((props) => {
      if (props.action === 'POP') {
        navigate(props.location.pathname, {
          state: {
            navFilter,
            navPagination,
            navSelectedRowKeys,
            navSelectedRows,
            navIsSelectAllAcrossPages,
            navExceptIds,
          },
        });
      }
    });
    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, []);

  useEffect(() => {
    if (!isNaN(Number(eqcTypeId))) {
      webEqcTypeList({
        variables: {
          filter: {
            eqcTypeId,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = nth(eqcTypeData?.eqcTypeList?.data, 0);
  const renderDownloadPdfButton = () => {
    return (
      <div
        id="export-pdf-btn"
        className="d-flex"
        onClick={async () => {
          const token = await getValidToken();
          const url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/report/eqc-types/${eqcTypeId}/export?access_token=Bearer ${token}&tenantId=${tenantId}`;
          Event(GA_EVENT.EXPORT_GLOBAL_CHECKLIST, {
            label: GA_LABEL.EXPORT_GLOBAL_CHECKLIST,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            checklist_id: eqcTypeId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          // eslint-disable-next-line no-undef
          window.open(url, '_blank');
        }}
      >
        PDF
      </div>
    );
  };
  const renderDownloadCsvButton = () => {
    return (
      <div
        id="export-csv-btn"
        className="d-flex"
        onClick={async () => {
          const token = await getValidToken();
          const url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/export-csv/report/eqc-types/${eqcTypeId}/export?access_token=Bearer ${token}&tenantId=${tenantId}`;
          Event(GA_EVENT.EXPORT_PROJECT_CHECKLIST_CSV, {
            label: GA_LABEL.EXPORT_PROJECT_CHECKLIST_CSV,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            checklist_id: eqcTypeId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          // eslint-disable-next-line no-undef
          window.open(url, '_blank');
        }}
      >
        CSV
      </div>
    );
  };
  if (isNaN(Number(eqcTypeId))) {
    return <Error404 />;
  }
  return (
    <div id="eqc-details-wrapper">
      {showModal && (
        <AddEditEqcModal
          showModal={showModal}
          setShowModal={setShowModal}
          setEqcData={setEqcData}
          eqcData={eqcData}
          isUpdate={!!eqcData}
          refetchUserDataWithInitialValues={refetch}
        />
      )}
      <div
        id="eqc-details-header"
        className="details-header d-flex justify-between page-header"
      >
        <h2 id="eqc-details-title" className="d-flex align-center">
          <span
            id="back-to-checklists-btn"
            onClick={() => {
              navigate(`${ROUTES.SETUP}${ROUTES.CHECKLISTS}`, {
                state: {
                  navFilter,
                  navPagination,
                  navSelectedRowKeys,
                  navSelectedRows,
                  navIsSelectAllAcrossPages,
                  navExceptIds,
                },
              });
            }}
          >
            <i id="back-icon" data-eva="pantone-outline" className="pointer" />
          </span>
          <ForwardArrow id="forward-arrow-icon" />
          <EllipsisText className="h1-typography" text={fetchData?.name} />
        </h2>
        <div id="eqc-details-actions" className="d-flex">
          <div id="eqc-import-btn" />
          <Dropdown
            id="export-dropdown"
            menu={{
              items: [
                {
                  key: 'pdf',
                  label: renderDownloadPdfButton(),
                },
                {
                  key: 'csv',
                  label: renderDownloadCsvButton(),
                },
              ],
            }}
            trigger={['click']}
            placement="bottom"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          >
            <Button
              id="export-btn"
              shape="round"
              icon={<Export height="26px" />}
              className="export-button d-flex b-0"
            >
              {isDesktopViewport && 'Export'}
            </Button>
          </Dropdown>
          <Button
            id="edit-eqc-btn"
            shape="round"
            type="primary"
            icon={<EditIcon />}
            className="edit-button-eqc d-flex"
            onClick={() => {
              setEqcData(fetchData);
              setShowModal(true);
            }}
          >
            {isDesktopViewport && 'Edit'}
          </Button>
        </div>
      </div>
      <div
        id="eqc-detail-content-wrapper"
        className={clsx(isDesktopViewport && 'mt-15')}
      >
        <EqcDetailContent eqcTypeData={fetchData} />
      </div>
    </div>
  );
};

export default EqcDetails;
